/**
 * /planner — Sift Trip Planner
 *
 * Motor de planificación de viajes: acepta queries como
 * "Bogotá → Europa 20 días $3,000" o "Japón anime 14 días"
 * y genera itinerarios multi-ciudad directamente.
 *
 * Diferencias respecto a /experiences:
 *  - Budget slider visible desde el inicio (no solo al mostrar resultados)
 *  - Ejemplos orientados a viajes completos, no solo actividades sueltas
 *  - Heroes copy distinto: "Planea tu viaje"
 *  - El sidebar de filtros aparece siempre (izquierda), no solo con resultados
 */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { track } from "@/lib/analytics";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import {
  generateExperienceSequence,
  parseIntent,
  wakeServer,
} from "@/lib/api";
import type { LLMParsedIntent } from "@/lib/api";
import { FilterSidebar } from "@/components/experiences/FilterSidebar";
import { IntentClarificationCard } from "@/components/experiences/IntentClarificationCard";
import { DateFlexPicker } from "@/components/experiences/DateFlexPicker";
import type { DateSelection } from "@/components/experiences/DateFlexPicker";
import { parseQuery, buildInterpretationText } from "@/lib/experiences/queryParser";
import type { ParsedQuery } from "@/lib/experiences/queryParser";
import type { ExperienceSequence } from "@/lib/types";
import { isPlannerPreviewEnabled } from "@/lib/experiences/launchFlags";

import { ExperienceTimeline } from "./experiences";

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/planner")({
  beforeLoad: () => {
    if (!isPlannerPreviewEnabled()) {
      throw redirect({ to: "/coming-soon/$vertical", params: { vertical: "planner" } });
    }
  },
  component: PlannerPage,
  head: () => ({
    meta: [
      { title: "Planea tu viaje — Sift Trip Planner" },
      {
        name: "description",
        content:
          "Describe tu viaje ideal — Sift Trip crea el itinerario completo. Multi-ciudad, con presupuesto, fechas flexibles.",
      },
    ],
  }),
});

// ── Example queries (planner-style) ──────────────────────────────────────────

const EXAMPLES = [
  { label: "Europa 3 semanas €2,500", icon: "🇪🇺" },
  { label: "Japón 14 días, anime + gastronomía", icon: "🇯🇵" },
  { label: "Portugal 8 días ruta de vinos", icon: "🇵🇹" },
  { label: "Sudeste Asiático 3 semanas $1,500", icon: "🌏" },
  { label: "Italia 10 días arte e historia", icon: "🇮🇹" },
  { label: "Luna de miel Grecia 10 días", icon: "🇬🇷" },
];

// ── Loading messages ──────────────────────────────────────────────────────────

const LOADING_MSGS = [
  "Analizando ciudades y conexiones…",
  "Calculando días óptimos por destino…",
  "Construyendo tu itinerario…",
  "Buscando experiencias reales…",
  "Organizando la secuencia ideal…",
];

// ── Budget helpers ────────────────────────────────────────────────────────────

function budgetToStyle(maxUsd: number): "economico" | "equilibrado" | "premium" | "lujo" | null {
  if (maxUsd <= 0)    return null;
  if (maxUsd <= 800)  return "economico";
  if (maxUsd <= 2000) return "equilibrado";
  if (maxUsd <= 5000) return "premium";
  return "lujo";
}

// ── Main page ─────────────────────────────────────────────────────────────────

function PlannerPage() {
  const [query,          setQuery]          = useState("");
  const [loading,        setLoading]        = useState(false);
  const [parsingIntent,  setParsingIntent]  = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [sequence,       setSequence]       = useState<ExperienceSequence | null>(null);
  const [parsed,         setParsed]         = useState<ParsedQuery | null>(null);
  const [interpretation, setInterpretation] = useState<string | null>(null);
  const [clarification,  setClarification]  = useState<LLMParsedIntent | null>(null);
  const [pendingCtx,     setPendingCtx]     = useState<undefined>(undefined);

  // Date picker
  const [dateSelection, setDateSelection] = useState<DateSelection>({ mode: "none", travelDate: null });

  // Sidebar filters — visible from the start
  const [budgetMin, setBudgetMin] = useState(0);
  const [budgetMax, setBudgetMax] = useState(3000);
  const [adults,    setAdults]    = useState(2);
  const [dirty,     setDirty]     = useState(false);

  // Loading message cycling
  const [msgIdx, setMsgIdx] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    wakeServer();
  }, []);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % LOADING_MSGS.length), 2200);
    return () => clearInterval(t);
  }, [loading]);

  // ── Generate sequence ──────────────────────────────────────────────────────
  const generateSequence = async (
    p: ParsedQuery,
    extra?: {
      pace?: string;
      budget_style?: string;
      budget_usd?: number;
      travel_date?: string | null;
      travelers?: number;
      has_children?: boolean;
      children_ages?: number[];
      travelers_type?: string;
      mobility_level?: "normal" | "low" | "minimal" | null;
    },
  ) => {
    setParsed(p);
    setLoading(true);
    setSequence(null);
    setError(null);
    setDirty(false);
    await wakeServer();
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);

    try {
      const result = await generateExperienceSequence({
        city:        p.city,
        city_iata:   p.iata,
        days:        p.days,
        interests:   p.interests,
        region:      p.region,
        travel_date: dateSelection.travelDate ?? extra?.travel_date ?? undefined,
        budget_style: extra?.budget_style ?? budgetToStyle(budgetMax) ?? undefined,
        budget_usd:  extra?.budget_usd ?? (budgetMax > 0 ? budgetMax : undefined),
        pace:        extra?.pace ?? p.pace ?? undefined,
        travelers:   extra?.travelers ?? adults,
        ...(import.meta.env.DEV ? { debug: true } : {}),
      });

      if (!result.success) {
        setError(result.error ?? "Error generando el itinerario.");
      } else {
        setSequence(result);
      }
    } catch {
      setError("Error de conexión. Intenta de nuevo en un momento.");
    } finally {
      setLoading(false);
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (rawQuery = query) => {
    const trimmed = rawQuery.trim();
    if (!trimmed) return;

    setClarification(null);
    setError(null);
    setInterpretation(null);

    setParsingIntent(true);
    let llmResult: LLMParsedIntent | null = null;
    try {
      llmResult = await parseIntent(trimmed);
    } catch { /* fallback to regex */ }
    setParsingIntent(false);

    // Client-side year detection when LLM fails
    if (!llmResult) {
      // Observe the fallback — only query_length is sent, never the raw query.
      track("intent_parser_fallback", { surface: "planner", query_length: trimmed.length });

      const HAS_MONTH_NO_YEAR =
        /\b\d{1,2}\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\b/i.test(trimmed) &&
        !/\b20\d{2}\b/.test(trimmed);
      if (HAS_MONTH_NO_YEAR) {
        const regexBase = parseQuery(trimmed);
        if (regexBase && (regexBase.iata || regexBase.region)) {
          const MONTH_MAP: Record<string, string> = {
            enero:"01", febrero:"02", marzo:"03", abril:"04", mayo:"05", junio:"06",
            julio:"07", agosto:"08", septiembre:"09", octubre:"10", noviembre:"11", diciembre:"12",
          };
          const mMatch = trimmed.match(/(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i);
          const partialDate = mMatch
            ? `-${MONTH_MAP[mMatch[2].toLowerCase()]}-${mMatch[1].padStart(2, "0")}`
            : null;
          const synthetic: LLMParsedIntent = {
            city: regexBase.city, iata: regexBase.iata, days: regexBase.days,
            hasDays: regexBase.hasDays, interests: regexBase.interests,
            pace: (regexBase.pace ?? null) as "suave" | "equilibrado" | "intenso" | null,
            budget: regexBase.budget ?? null,
            mobilityLevel: (regexBase.mobilityLevel ?? "normal") as "normal" | "low" | "minimal",
            travelersType: null, travel_date: partialDate,
            region: regexBase.region ?? null, regionLabel: regexBase.regionLabel ?? null,
            interpretations: regexBase.interpretations,
            ambiguities: ["año"], follow_up: "¿En qué año planeas viajar?",
            parsed_by: "llm" as const,
          };
          setClarification(synthetic);
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
          return;
        }
      }
    }

    // LLM found ambiguity → clarification card
    if (llmResult?.follow_up) {
      setClarification(llmResult);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      return;
    }

    // Build ParsedQuery from LLM or regex
    const pFromLLM: ParsedQuery | null =
      llmResult && (llmResult.iata || llmResult.region)
        ? {
            city: llmResult.city, iata: llmResult.iata, days: llmResult.days,
            hasDays: llmResult.hasDays, interests: llmResult.interests,
            pace: llmResult.pace ?? null, budget: llmResult.budget ?? null,
            mobilityLevel: llmResult.mobilityLevel,
            region: llmResult.region ?? undefined, regionLabel: llmResult.regionLabel ?? undefined,
            interpretations: llmResult.interpretations,
          }
        : null;

    const p = pFromLLM ?? parseQuery(trimmed);
    if (!p) {
      setError('No pude entender el destino. Intenta: "Lisboa 5 días gastronomía"');
      return;
    }

    setInterpretation(buildInterpretationText(p.interpretations));
    await generateSequence(p, {
      travel_date: llmResult?.travel_date ?? undefined,
      pace: p.pace ?? undefined,
      budget_style: p.budget ?? budgetToStyle(budgetMax) ?? undefined,
      budget_usd: budgetMax > 0 ? budgetMax : undefined,
      travelers: adults,
    });
  };

  // ── Clarification resolve ──────────────────────────────────────────────────
  const handleClarificationResolve = async (enriched: LLMParsedIntent) => {
    setClarification(null);
    const p: ParsedQuery = {
      city: enriched.city, iata: enriched.iata, days: enriched.days,
      hasDays: enriched.hasDays, interests: enriched.interests,
      pace: enriched.pace ?? null, budget: enriched.budget ?? null,
      mobilityLevel: enriched.mobilityLevel,
      region: enriched.region ?? undefined, regionLabel: enriched.regionLabel ?? undefined,
      interpretations: enriched.interpretations,
    };
    setInterpretation(buildInterpretationText(p.interpretations));
    await generateSequence(p, { travel_date: enriched.travel_date ?? undefined });
  };

  const handleClarificationDismiss = async () => {
    const llmRes = clarification;
    setClarification(null);
    if (!llmRes || (!llmRes.iata && !llmRes.region)) return;
    const p: ParsedQuery = {
      city: llmRes.city, iata: llmRes.iata, days: llmRes.days,
      hasDays: llmRes.hasDays, interests: llmRes.interests,
      pace: llmRes.pace ?? null, budget: llmRes.budget ?? null,
      mobilityLevel: llmRes.mobilityLevel,
      region: llmRes.region ?? undefined, regionLabel: llmRes.regionLabel ?? undefined,
      interpretations: llmRes.interpretations,
    };
    setInterpretation(buildInterpretationText(p.interpretations));
    await generateSequence(p, { travel_date: llmRes.travel_date ?? undefined });
  };

  const handleApplyFilters = async () => {
    if (!parsed) return;
    setDirty(false);
    await generateSequence(parsed, {
      budget_style: budgetToStyle(budgetMax) ?? undefined,
      budget_usd: budgetMax > 0 ? budgetMax : undefined,
      travelers: adults,
    });
  };

  const compact = loading || !!sequence || !!clarification;

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: "var(--color-background)" }}>
      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[140px]" style={{ background: "oklch(0.580 0.220 256 / 0.05)" }} />
        <div className="absolute bottom-1/4 -left-48 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: "oklch(0.298 0.058 167 / 0.04)" }} />
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section
        className={`relative transition-all duration-700 ${compact ? "pt-8 pb-6" : "pt-20 pb-16"}`}
        style={{
          background: compact
            ? "linear-gradient(180deg, oklch(0.16 0.04 240) 0%, transparent 100%)"
            : "linear-gradient(180deg, oklch(0.12 0.06 240) 0%, oklch(0.08 0.04 240) 60%, transparent 100%)",
        }}
      >
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6">
          {!compact && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mb-10"
            >
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 text-xs font-semibold tracking-widest uppercase"
                style={{ background: "oklch(0.298 0.058 167 / 0.15)", color: "oklch(0.60 0.12 167)", border: "1px solid oklch(0.298 0.058 167 / 0.25)" }}>
                ✦ Sift Trip Planner
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl font-black mb-4 leading-tight"
                style={{ color: "oklch(0.97 0.010 72)" }}>
                Describe tu viaje.<br />
                <span style={{ color: "oklch(0.60 0.12 167)" }}>Sift Trip lo planea.</span>
              </h1>
              <p className="text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
                style={{ color: "oklch(0.72 0.020 60)" }}>
                Multi-ciudad · Presupuesto real · Experiencias verificadas
              </p>
            </motion.div>
          )}

          {/* ── Budget slider (always visible) ──────────────────────────── */}
          {!compact && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mb-5"
            >
              <BudgetSliderInline
                min={budgetMin} max={budgetMax}
                onChange={(min, max) => { setBudgetMin(min); setBudgetMax(max); }}
                adults={adults}
                onAdultsChange={setAdults}
              />
            </motion.div>
          )}

          {/* ── Search bar ───────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <PlannerSearchBar
              query={query}
              onChange={setQuery}
              onSubmit={handleSubmit}
              loading={loading || parsingIntent}
              dateSelection={dateSelection}
              onDateChange={setDateSelection}
              interpretation={interpretation}
              error={error}
            />
          </motion.div>

          {/* ── Example chips ─────────────────────────────────────────────── */}
          {!compact && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              className="flex flex-wrap gap-2 justify-center mt-4"
            >
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.label}
                  type="button"
                  onClick={() => { setQuery(ex.label); handleSubmit(ex.label); }}
                  className="text-xs px-3.5 py-1.5 rounded-full transition-all hover:scale-[1.04] active:scale-[0.97]"
                  style={{
                    background: "oklch(0.97 0.008 72 / 0.07)",
                    color:      "oklch(0.78 0.016 72)",
                    border:     "1px solid oklch(0.97 0.008 72 / 0.14)",
                  }}
                >
                  {ex.icon} {ex.label}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ── Content area ───────────────────────────────────────────────────── */}
      <div
        ref={resultsRef}
        className={`relative z-10 px-4 sm:px-6 pb-28 mx-auto transition-all duration-500 ${sequence && !loading ? "max-w-5xl" : "max-w-3xl"}`}
      >
        {/* Clarification card */}
        <AnimatePresence>
          {clarification && !loading && (
            <IntentClarificationCard
              llmResult={clarification}
              currentYear={new Date().getFullYear()}
              onResolve={(e) => { void handleClarificationResolve(e); }}
              onDismiss={() => { void handleClarificationDismiss(); }}
            />
          )}
        </AnimatePresence>

        {/* Loading */}
        <AnimatePresence>
          {loading && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
              className="flex flex-col items-center py-20 gap-6"
            >
              <div className="relative w-14 h-14">
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent"
                  style={{ borderTopColor: "oklch(0.298 0.058 167)", animation: "spin 0.9s linear infinite" }} />
                <div className="absolute inset-1.5 rounded-full border-[2px] border-transparent"
                  style={{ borderTopColor: "oklch(0.58 0.12 167 / 0.45)", animation: "spin 1.4s linear infinite reverse" }} />
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={msgIdx}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28 }}
                  className="text-sm font-medium"
                  style={{ color: "oklch(0.545 0.020 60 / 0.70)" }}
                >
                  {LOADING_MSGS[msgIdx]}
                </motion.p>
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results: left sidebar + right timeline */}
        <AnimatePresence>
          {sequence && !loading && (
            <motion.div
              key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              exit={{ opacity: 0 }} transition={{ duration: 0.30 }}
            >
              <div className="flex gap-6 items-start">
                {/* Left — FilterSidebar */}
                <div className="w-56 shrink-0 hidden lg:block sticky top-24 self-start">
                  <FilterSidebar
                    budgetMin={budgetMin}
                    budgetMax={budgetMax}
                    adults={adults}
                    loading={loading}
                    dirty={dirty}
                    onBudgetChange={(min, max) => { setBudgetMin(min); setBudgetMax(max); setDirty(true); }}
                    onAdultsChange={(n) => { setAdults(n); setDirty(true); }}
                    onApply={() => { void handleApplyFilters(); }}
                  />
                </div>

                {/* Right — Timeline */}
                <div className="flex-1 min-w-0">
                  <ExperienceTimeline
                    sequence={sequence}
                    familyMode={false}
                    onNewSearch={() => { setSequence(null); setQuery(""); setError(null); setInterpretation(null); }}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Spin keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Inline budget + travelers bar (shown in hero before results) ──────────────

interface BudgetSliderInlineProps {
  min: number; max: number;
  onChange: (min: number, max: number) => void;
  adults: number;
  onAdultsChange: (n: number) => void;
}

function BudgetSliderInline({ min, max, onChange, adults, onAdultsChange }: BudgetSliderInlineProps) {
  const [lMin, setLMin] = useState(min);
  const [lMax, setLMax] = useState(max);

  const fmtUsd = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `$${v}`;
  const minPct = (lMin / 10000) * 100;
  const maxPct = (lMax / 10000) * 100;
  const commit = () => onChange(lMin, lMax);

  return (
    <>
      <style>{`
        .plan-range {
          -webkit-appearance: none; appearance: none;
          background: transparent; pointer-events: none;
          position: absolute; inset: 0; width: 100%; height: 100%; margin: 0;
        }
        .plan-range::-webkit-slider-runnable-track { background: transparent; height: 6px; }
        .plan-range::-moz-range-track { background: transparent; height: 6px; }
        .plan-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 18px; height: 18px; border-radius: 50%;
          background: oklch(0.298 0.058 167);
          border: 2.5px solid oklch(0.18 0.04 240);
          box-shadow: 0 1px 6px oklch(0.298 0.058 167 / 0.50);
          pointer-events: all; cursor: grab;
        }
        .plan-range::-moz-range-thumb {
          width: 18px; height: 18px; border-radius: 50%;
          background: oklch(0.298 0.058 167);
          border: 2.5px solid oklch(0.18 0.04 240);
          pointer-events: all; cursor: grab;
        }
      `}</style>

      <div
        className="rounded-2xl px-5 py-4 flex flex-col sm:flex-row gap-5 items-center sm:items-end"
        style={{
          background: "oklch(0.97 0.008 72 / 0.06)",
          border: "1px solid oklch(0.97 0.008 72 / 0.12)",
        }}
      >
        {/* Budget section */}
        <div className="flex-1 w-full">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-xs font-semibold tracking-wide uppercase"
              style={{ color: "oklch(0.72 0.020 60)" }}>
              Presupuesto total
            </p>
            <p className="text-sm font-bold tabular-nums"
              style={{ color: "oklch(0.60 0.12 167)" }}>
              {fmtUsd(lMin)} – {fmtUsd(lMax)}
            </p>
          </div>
          <div className="relative h-[18px] flex items-center mx-1">
            <div className="absolute inset-x-0 h-1.5 rounded-full" style={{ background: "oklch(0.97 0.008 72 / 0.18)" }} />
            <div className="absolute h-1.5 rounded-full" style={{
              left: `${minPct}%`, width: `${maxPct - minPct}%`,
              background: "oklch(0.298 0.058 167)",
            }} />
            <input type="range" className="plan-range"
              style={{ zIndex: lMin > 9000 ? 5 : 3 }}
              min={0} max={10000} step={100}
              value={lMin}
              onChange={(e) => setLMin(Math.min(Number(e.target.value), lMax - 100))}
              onMouseUp={commit} onTouchEnd={commit} />
            <input type="range" className="plan-range"
              style={{ zIndex: lMin > 9000 ? 3 : 5 }}
              min={0} max={10000} step={100}
              value={lMax}
              onChange={(e) => setLMax(Math.max(Number(e.target.value), lMin + 100))}
              onMouseUp={commit} onTouchEnd={commit} />
          </div>
          <div className="flex justify-between mt-1 px-0.5">
            <span className="text-[10px]" style={{ color: "oklch(0.72 0.020 60 / 0.50)" }}>$0</span>
            <span className="text-[10px]" style={{ color: "oklch(0.72 0.020 60 / 0.50)" }}>$10k</span>
          </div>
        </div>

        {/* Travelers */}
        <div className="flex items-center gap-3 shrink-0">
          <p className="text-xs font-semibold tracking-wide uppercase"
            style={{ color: "oklch(0.72 0.020 60)" }}>
            Viajeros
          </p>
          <button type="button"
            onClick={() => onAdultsChange(Math.max(1, adults - 1))}
            disabled={adults <= 1}
            className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-95 disabled:opacity-30"
            style={{ background: "oklch(0.97 0.008 72 / 0.12)", color: "oklch(0.85 0.016 72)" }}
          >−</button>
          <span className="text-sm font-bold w-4 text-center tabular-nums"
            style={{ color: "oklch(0.90 0.012 72)" }}>
            {adults}
          </span>
          <button type="button"
            onClick={() => onAdultsChange(Math.min(12, adults + 1))}
            disabled={adults >= 12}
            className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-all hover:scale-110 active:scale-95 disabled:opacity-30"
            style={{ background: "oklch(0.97 0.008 72 / 0.12)", color: "oklch(0.85 0.016 72)" }}
          >+</button>
        </div>
      </div>
    </>
  );
}

// ── Planner search bar ────────────────────────────────────────────────────────

interface PlannerSearchBarProps {
  query: string;
  onChange: (q: string) => void;
  onSubmit: (q?: string) => void;
  loading: boolean;
  dateSelection: DateSelection;
  onDateChange: (d: DateSelection) => void;
  interpretation: string | null;
  error: string | null;
}

function PlannerSearchBar({
  query, onChange, onSubmit, loading,
  dateSelection, onDateChange, interpretation, error,
}: PlannerSearchBarProps) {
  return (
    <div className="space-y-2">
      <div
        className="flex items-center gap-3 rounded-2xl px-4 py-3.5 transition-all"
        style={{
          background: "oklch(0.97 0.008 72 / 0.09)",
          border:     "1.5px solid oklch(0.97 0.008 72 / 0.18)",
          boxShadow:  "0 0 0 0 transparent",
        }}
        onFocus={() => {}}
      >
        {/* Sparkle icon */}
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0" style={{ color: "oklch(0.58 0.12 167)" }}>
          <path d="M9 1l1.8 5.6L17 8l-6.2 1.4L9 17l-1.8-7.6L1 8l6.2-1.4L9 1z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="oklch(0.58 0.12 167 / 0.18)"/>
        </svg>

        <input
          type="text"
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !loading && onSubmit()}
          placeholder="Ej: Europa 3 semanas en octubre, €2,500 para 2 personas"
          className="flex-1 bg-transparent outline-none text-sm font-medium placeholder:font-normal"
          style={{ color: "oklch(0.92 0.010 72)", caretColor: "oklch(0.58 0.12 167)" }}
        />

        <button
          type="button"
          onClick={() => !loading && onSubmit()}
          disabled={loading || !query.trim()}
          className="shrink-0 flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-40"
          style={{ background: "oklch(0.298 0.058 167)" }}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Planando…
            </span>
          ) : (
            <>planear →</>
          )}
        </button>
      </div>

      {/* Date picker */}
      <div className="flex justify-end pr-1">
        <DateFlexPicker
          value={dateSelection}
          onChange={onDateChange}
        />
      </div>

      {/* Interpretation */}
      {interpretation && (
        <p className="text-xs px-1" style={{ color: "oklch(0.58 0.12 167 / 0.85)" }}>
          ✦ {interpretation}
        </p>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs px-1" style={{ color: "oklch(0.62 0.18 25)" }}>
          {error}
        </p>
      )}
    </div>
  );
}
